import React from "react";
import { Link } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import StateBadge from "@/components/StateBadge";
import { ArrowRight, Coffee, ShoppingBag, Dumbbell, Hotel, Building, Store, Briefcase, GraduationCap, Cross, Plane, TrainFront, Film, Trophy } from "lucide-react";

const TYPES = [
  { icon: Coffee, name: "Cafés" },
  { icon: Store, name: "Restaurants" },
  { icon: Dumbbell, name: "Gyms" },
  { icon: Hotel, name: "Hotels" },
  { icon: ShoppingBag, name: "Malls" },
  { icon: Store, name: "Retail Stores" },
  { icon: Briefcase, name: "Offices" },
  { icon: Building, name: "Coworking" },
  { icon: GraduationCap, name: "Universities" },
  { icon: Cross, name: "Hospitals" },
  { icon: Plane, name: "Airports" },
  { icon: TrainFront, name: "Metro & Rail" },
  { icon: Film, name: "Cinemas" },
  { icon: Trophy, name: "Stadiums" },
];

export default function Venues() {
  return (
    <PublicShell>
      <section className="pt-28 md:pt-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16">
          <div className="flex items-center gap-3">
            <StateBadge state="BETA" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">Venues</span>
          </div>
          <h1 className="mt-6 font-display font-black tracking-tighter leading-[0.9] text-5xl md:text-7xl">
            Turn your venue into
            <br />
            <span className="text-white/30">a media network.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-white/60 text-lg">
            A venue is not simply inventory. The venue <em>is</em> the medium.
            Mirrorloops helps you monetise screens while protecting the
            atmosphere and usefulness of your space.
          </p>
          <div className="mt-10 flex gap-3 flex-wrap">
            <Link to="/register" data-testid="venues-cta-connect" className="px-6 py-3 bg-white text-black font-semibold hover:bg-white/90">
              Connect your venue
            </Link>
            <Link to="/calculator" data-testid="venues-cta-calc" className="px-6 py-3 border border-white/20 text-white hover:border-white">
              Estimate revenue
            </Link>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
          <div className="text-[11px] uppercase tracking-[0.22em] text-white/40 mb-4">Every kind of place</div>
          <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl md:text-6xl mb-10">
            If people gather, Mirrorloops can plug in.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-white/10 border border-white/10" data-testid="venues-grid">
            {TYPES.map((t) => {
              const I = t.icon;
              return (
                <div key={t.name} className="bg-[#0A0908] p-6 min-h-[130px] flex flex-col justify-between hover:bg-white/[0.03] transition">
                  <I size={20} className="text-[#E8D6A3]" />
                  <div className="mt-4">
                    <div className="text-sm font-bold">{t.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mt-1">
                      Venue type
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-[#0d0c0b]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {[
            ["Protect atmosphere", "Set rules about which categories, tones, or intensities are allowed on your screens — Mirrorloops enforces them at the network level."],
            ["Fair revenue share", "Transparent CPM · monthly settlement · proof of play. You always see what ran on your screen and what it earned."],
            ["Zero hardware lock-in", "Any modern display + a browser = a Mirrorloops endpoint. Keep the screens you already own."],
          ].map(([t, d]) => (
            <div key={t} className="bg-[#0A0908] p-8 min-h-[220px]">
              <div className="font-display font-extrabold text-2xl mb-3">{t}</div>
              <div className="text-white/60 leading-relaxed text-sm">{d}</div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 text-center">
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl">
            Ready to plug in?
          </h2>
          <Link to="/register" className="mt-8 inline-flex items-center gap-2 px-6 py-4 bg-white text-black font-bold">
            Connect your venue <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </PublicShell>
  );
}
